import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../Auth/AuthContext';
import {fetchBrands} from '../../utils/brandsApi';

const ITEMS_PER_PAGE = 24;

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '24px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        marginBottom: '16px'
    },
    breadcrumbLink: {
        color: '#666',
        textDecoration: 'none',
        cursor: 'pointer'
    },
    breadcrumbSeparator: {
        color: '#999'
    },
    title: {
        fontSize: '32px',
        fontWeight: 700,
        color: '#1a1a1a',
        margin: '0 0 24px 0'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '10px'
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        minHeight: '250px'
    },
    imageWrapper: {
        width: '100%',
        paddingTop: '70%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
    },
    image: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imagePlaceholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e5e7eb',
        color: '#9ca3af',
        fontSize: '14px',
        fontWeight: 500
    },
    info: {
        padding: '12px 14px 14px 14px',
        display: 'flex',
        flexDirection: 'column'
    },
    brandName: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '6px',
        lineHeight: 1.4
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '4px',
        fontSize: '12px',
        color: '#6b7280',
        marginTop: 'auto'
    },
    rating: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 500
    },
    star: {
        color: '#fbbf24'
    },
    dot: {
        opacity: 0.7
    },
    location: {
        color: '#6b7280'
    },
    // Loading & error states
    stateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        fontSize: '15px',
        color: '#6b7280'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '12px'
    },
    errorText: {
        fontSize: '15px',
        color: '#ef4444'
    },
    retryButton: {
        padding: '8px 20px',
        backgroundColor: '#111827',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    emptyText: {
        fontSize: '15px',
        color: '#9ca3af'
    },
    // Pagination
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '32px',
        fontSize: '14px',
        color: '#374151'
    },
    pageButton: {
        padding: '8px 16px',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        color: '#374151',
        transition: 'background-color 0.15s ease'
    },
    pageButtonDisabled: {
        padding: '8px 16px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'default',
        color: '#d1d5db'
    }
};

// Shimmer skeleton card for loading state
function SkeletonCard() {
    return (
        <div style={{...styles.card, cursor: 'default', animation: 'none'}}>
            <div style={{...styles.imageWrapper, backgroundColor: '#e5e7eb'}}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite'
                }} />
            </div>
            <div style={styles.info}>
                <div style={{
                    width: '70%',
                    height: '14px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    marginBottom: '8px'
                }} />
                <div style={{
                    width: '50%',
                    height: '12px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px'
                }} />
            </div>
        </div>
    );
}

export default function Brands() {
    const navigate = useNavigate();
    const {tokens} = useAuth();

    const [brands, setBrands] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

    const loadBrands = useCallback(async (pageNum) => {
        setLoading(true);
        setError(null);
        const result = await fetchBrands({
            page: pageNum,
            limit: ITEMS_PER_PAGE,
            accessToken: tokens?.accessToken
        });
        if (result.ok && result.data) {
            setBrands(result.data.brands ?? []);
            setTotal(result.data.total ?? 0);
        } else {
            setError(result.error || 'Failed to load brands');
        }
        setLoading(false);
    }, [tokens?.accessToken]);

    useEffect(() => {
        loadBrands(page);
    }, [page, loadBrands]);

    const handleBrandClick = (brand) => {
        navigate(`/product-gallery/${brand.slug}`);
    };

    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    // Render loading skeleton
    const renderLoading = () => (
        <div style={styles.grid}>
            {Array.from({length: 8}).map((_, i) => (
                <SkeletonCard key={i} />
            ))}
            <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
        </div>
    );

    // Render error state
    const renderError = () => (
        <div style={styles.errorContainer}>
            <div style={styles.errorText}>{error}</div>
            <button style={styles.retryButton} onClick={() => loadBrands(page)}>Retry</button>
        </div>
    );

    // Render empty state
    const renderEmpty = () => (
        <div style={styles.stateContainer}>
            <span style={styles.emptyText}>No brands found</span>
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.breadcrumb}>
                
                
            <span>Brands</span>
            </div>

            <h1 style={styles.title}>Brands</h1>

            {loading && renderLoading()}

            {!loading && error && renderError()}

            {!loading && !error && brands.length === 0 && renderEmpty()}

            {!loading && !error && brands.length > 0 && (
                <>
                    <div style={styles.grid}>
                        {brands.map((brand) => (
                            <div
                                key={brand.id}
                                style={styles.card}
                                onClick={() => handleBrandClick(brand)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.1)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.04)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <div style={styles.imageWrapper}>
                                    {brand.imageUrl
                                        ? <img src={brand.imageUrl} alt={brand.name} style={styles.image} />
                                        : <div style={styles.imagePlaceholder}>{brand.name?.charAt(0) || '?'}</div>
                                    }
                                </div>
                                <div style={styles.info}>
                                    <div style={styles.brandName}>{brand.name}</div>
                                    <div style={styles.metaRow}>
                                        <span style={styles.rating}>
                                            <span style={styles.star}>&#9733;</span>{' '}
                                            {brand.rating != null ? brand.rating.toFixed(1) : '0'}{' '}
                                            ({brand.reviews ?? 0})
                                        </span>
                                        {brand.location && (
                                            <>
                                                <span style={styles.dot}>&bull;</span>
                                                <span style={styles.location}>{brand.location}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div style={styles.pagination}>
                            <button
                                style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
                                onClick={handlePrevPage}
                                disabled={page <= 1}
                            >
                                &larr; Previous
                            </button>
                            <span>Page {page} of {totalPages}</span>
                            <button
                                style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
                                onClick={handleNextPage}
                                disabled={page >= totalPages}
                            >
                                Next &rarr;
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
